import { useEffect, useMemo, useRef, useState } from "react";
import { ButtonGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Button from "../../common/components/Button";
import DefaultButton from "../../common/components/DefaultButton";
import { Col, Row } from "../../common/components/Grid";
import InfiniteScroll from "../../common/components/InfiniteScroll";
import { SettingsDisplay, useSettingsContext } from "../../common/components/SettingsPanels";
import { SortDialog } from "../../common/components/Sort";
import TextInput from "../../common/components/TextInput";
import { dynamicFilter, dynamicSort } from "../../common/utils/tableUtilities";
import PropTypes from 'prop-types';
import { deepClone } from "../../common/utils/objectUtilities";

const NewItemButton = ({ selected, onClick }) => {
    const { pushPanel } = useSettingsContext();
    return <DefaultButton className="m-2" kind="add" variant={selected ? "primary" : "outline-primary"} onClick={() => { pushPanel(1); onClick(); }}></DefaultButton>
}
NewItemButton.propTypes = {
    selected: PropTypes.bool,
    onClick: PropTypes.func
}

const ItemButton = ({ ItemLabel, value, selected, selectItem }) => {
    const { pushPanel } = useSettingsContext();
    return <Button className="m-2" variant={selected ? "dark" : "outline-dark"} onClick={() => { pushPanel(1, () => selectItem()); selectItem(value); }}>
        <ItemLabel value={value}></ItemLabel>
    </Button>
}
ItemButton.propTypes = {
    ItemLabel: PropTypes.node,
    value: PropTypes.object,
    selected: PropTypes.bool,
    selectItem: PropTypes.func
}

const SimpleSettings = ({ data, uuid, label, ItemLabel, ItemDetails, pkey, defaultSort, sortFields, templateItem, additionalControls, checkValidity, disableCopy = false, canAdd, canUpdate, canRemove, onAdd, onChange, onRemove, onSelect, onClose }) => {
    const { t } = useTranslation();
    const [selectedItem, setSelectedItem] = useState();
    const [query, setQuery] = useState();
    const [sort, setSort] = useState(defaultSort);
    const [showSortDialog, setShowSortDialog] = useState(false);
    const [newEntry, setNewEntry] = useState(deepClone(templateItem));
    const isNew = !selectedItem;
    const item = isNew ? newEntry : data?.find(entry => entry[pkey] === selectedItem);
    const canCreate = canAdd(newEntry);
    const disabled = isNew ? !onAdd : !canUpdate(item);
    const formRef = useRef();

    const clearData = () => {
        setSelectedItem();
        setNewEntry(deepClone(templateItem))
    }

    const displayData = useMemo(() => {
        let displayData = [...data];
        displayData.sort(dynamicSort(sort));
        if (query) {
            displayData = dynamicFilter(displayData, query);
        }
        return displayData;
    }, [query, data, sort]);

    useEffect(() => {
        if (displayData?.length > 0 && !canCreate && !selectedItem) {
            setSelectedItem(displayData[0][pkey]);
        }
    }, [displayData, canCreate, selectedItem, pkey])

    const isValid = () => formRef.current?.checkValidity && (!checkValidity || checkValidity(item));

    const handleUpdate = delta => {
        if (isNew) {
            setNewEntry(newEntry => ({ ...newEntry, ...delta }));
        } else {
            if (isValid()) {
                onChange(item, delta);
            }
        }
    }

    const handleCreate = () => {
        if (isValid()) {
            onAdd(newEntry).then(entry => {
                clearData();
                setSelectedItem(entry[pkey]);
            });
        }
    }

    const handleSelect = async (item) => {
        if (onSelect) {
            await onSelect()
        }
        clearData();
        setSelectedItem(item?.[pkey]);
    }

    const handleCopy = () => {
        clearData();
        const copiedItem = deepClone(item);
        delete copiedItem[pkey];
        setNewEntry(copiedItem);
    }

    const controls = !disabled && <Row>
        <Col className="ml-1">
            {isNew ? <DefaultButton kind="clear" onClick={() => clearData()}></DefaultButton> : (canRemove(item) && <DefaultButton kind="delete" onClick={() => { onRemove(item); clearData(); }}></DefaultButton>)}
        </Col>
        <Col className="text-right">
            <span className="mx-2">{additionalControls?.(item, handleSelect)}</span>
            {isNew && <DefaultButton kind="add" onClick={handleCreate}></DefaultButton>}
            {!isNew && !disableCopy && <DefaultButton kind="copy" onClick={handleCopy}></DefaultButton>}
        </Col>
    </Row>

    return <>
        <SortDialog show={showSortDialog} defaultOrder={defaultSort} sort={sort} fields={sortFields} title={t('sort')} onClose={newSort => { if (newSort) { setSort(newSort); } setShowSortDialog(false); }} placeholder={t('sort')}></SortDialog>
        <SettingsDisplay uuid={uuid} title={label} onClose={onClose} defaultSizes={[25,75]}>
            <div className="w-100 h-100 d-flex flex-column">
                    <Row>
                        <Col>
                            <TextInput value={query} live={true} hideLabel={true} placeholder={t('filter', { count: 1 })} onChange={setQuery}
                                prependCtrl={
                                    <ButtonGroup>
                                        <DefaultButton kind="sort" onClick={() => setShowSortDialog(true)} hideLabel={true}></DefaultButton>
                                    </ButtonGroup>
                                }>
                            </TextInput>
                        </Col>
                    </Row>
                {canCreate && <NewItemButton selected={isNew} onClick={() => clearData()}></NewItemButton>}
                <InfiniteScroll data={displayData} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                    renderItem={item => {
                        return <ItemButton ItemLabel={ItemLabel} key={item[pkey]} selected={selectedItem === item[pkey]} value={item} selectItem={item => handleSelect(item)}></ItemButton>
                    }}></InfiniteScroll>
            </div>
            <div className="ml-2 mr-0 h-100 d-flex flex-column">
                {controls}
                <div className="mt-2" style={{ flex: 1, overflowY: 'auto' }}>
                    <ItemDetails ref={formRef} value={item} disabled={disabled} isNew={isNew} onChange={handleUpdate}></ItemDetails>
                </div>
            </div>
        </SettingsDisplay>
    </>
}
SimpleSettings.propTypes = {
    data: PropTypes.any,
    uuid: PropTypes.string,
    label: PropTypes.string,
    ItemLabel: PropTypes.node,
    ItemDetails: PropTypes.node,
    pkey: PropTypes.string,
    defaultSort: PropTypes.arrayOf(PropTypes.string),
    sortFields: PropTypes.object,
    templateItem: PropTypes.object,
    additionalControls: PropTypes.node,
    checkValidity: PropTypes.func,
    canAdd: PropTypes.func,
    canUpdate: PropTypes.func,
    canRemove: PropTypes.func,
    onAdd: PropTypes.func,
    onChange: PropTypes.func,
    onRemove: PropTypes.func,
    onSelect: PropTypes.func,
    onClose: PropTypes.func
}

export default SimpleSettings;